import type { FeaturedProduct, PdpProduct } from "@jog/types"
import { asLink, isFilled } from "@prismicio/helpers"
import type { LinkField, Slice } from "@prismicio/types"

import { getPdp } from "../aws/commerce"
import { typedBoolean } from "../helper"
import { RedisCache } from "../redis/client"

const fetchFeaturedProducts = async (_paths: string[], locale: string) => {
    const redisCache = new RedisCache()
    const paths = [...new Set(_paths)]
    const ids = await Promise.all(paths.map((path: string) => redisCache.getProductId(path, locale).catch(() => null)))
    const productsData: { product: PdpProduct; path: string }[] = (
        await Promise.all(
            ids.filter(typedBoolean).map(({ id, path }) => {
                if (!id) return null
                return getPdp(id)
                    .then((product) => ({ product, path }))
                    .catch(() => null)
            })
        )
    ).filter(typedBoolean)
    const products: FeaturedProduct[] = productsData
        .map(({ product, path }) => {
            const name = ((product.name?.[process.env.NEXT_PUBLIC_LOCALE!] || product.name?.["en"]) as string) ?? ""
            if (!name) return
            const isOnStock = product.sizeGroup?.sizes?.some((item) => item.isOnStock)
            if (!isOnStock) return
            const price = product.price
            if (!price) return
            return {
                price,
                firstPrice: product.firstPrice || null,
                lowestPrice: product.lowestPrice || null,
                discountedPrice: product.discountedPrice || null,
                name,
                path: `/${path}`.replace(/^(\/*)/, "/"),
                isOnStock,
                attributes: product.attributes,
                promotionalBanner: product.promotionalBanner?.name?.[process.env.NEXT_PUBLIC_LOCALE!] || "",
                id: product.id,
            }
        })
        .filter(typedBoolean)
    return products
}

export const buildFeaturedProducts = async <T extends Slice<any, any, any>>(
    prismic: {
        data: {
            body: (
                | {
                      slice_type: "featured_products" | "carousel_products"
                      items: { product_link: LinkField }[]
                  }
                | T
            )[]
        }
    },
    locale: string
) => {
    if (!prismic.data.body?.length) return []
    const paths = [...prismic.data.body]
        .map((slice) => {
            switch (slice.slice_type) {
                case "featured_products":
                case "carousel_products":
                    return slice.items.map((link) => {
                        if (!isFilled.link(link.product_link)) return ""
                        const url = asLink(link.product_link)
                        if (!url) return ""
                        try {
                            return new URL(url).pathname.replace(/^\//, "")
                        } catch {
                            return ""
                        }
                    })
                default:
                    return ""
            }
        })
        .filter(typedBoolean)
        .flat()
    return fetchFeaturedProducts(paths, locale)
}
